/**
 * AUDIO — monologue "radio voice" treatment: a band-passed noise murmur with
 * speech-rate amplitude modulation under the UI caption. Completion timing
 * (monologue:done) is owned by index.ts, not here. Owned by AUDIO.
 */
import {
  MURMUR_AM_HZ,
  MURMUR_BANDPASS_HZ,
  MURMUR_FADE_S,
  MURMUR_GAIN,
  MURMUR_Q
} from './constants'
import type { AudioEngine } from './engine'

const AM_DEPTH = 0.5
const MS_PER_S = 1000

export const playMurmur = (engine: AudioEngine, durationMs: number): void => {
  const { ctx, master, noiseBuffer } = engine
  const now = ctx.currentTime
  const durationS = durationMs / MS_PER_S
  const end = now + durationS

  const source = ctx.createBufferSource()
  source.buffer = noiseBuffer
  source.loop = true

  const bandpass = ctx.createBiquadFilter()
  bandpass.type = 'bandpass'
  bandpass.frequency.value = MURMUR_BANDPASS_HZ
  bandpass.Q.value = MURMUR_Q

  // Speech-rate tremolo: gain sits at AM_DEPTH, LFO swings it 0..1.
  const tremolo = ctx.createGain()
  tremolo.gain.value = AM_DEPTH
  const lfo = ctx.createOscillator()
  lfo.frequency.value = MURMUR_AM_HZ
  const lfoDepth = ctx.createGain()
  lfoDepth.gain.value = AM_DEPTH
  lfo.connect(lfoDepth)
  lfoDepth.connect(tremolo.gain)

  const envelope = ctx.createGain()
  envelope.gain.setValueAtTime(0, now)
  envelope.gain.linearRampToValueAtTime(MURMUR_GAIN, now + MURMUR_FADE_S)
  envelope.gain.setValueAtTime(MURMUR_GAIN, Math.max(now + MURMUR_FADE_S, end - MURMUR_FADE_S))
  envelope.gain.linearRampToValueAtTime(0, end)

  source.connect(bandpass)
  bandpass.connect(tremolo)
  tremolo.connect(envelope)
  envelope.connect(master)

  source.start(now)
  lfo.start(now)
  source.stop(end)
  lfo.stop(end)
}
